/**
 * Куда вести вошедшего — «по роли и активности» (решение владельца, 25.09.2026).
 *
 * Раньше любого вошедшего вели в `/cabinet` — «Книгу друзей», то есть в CRM:
 * клиент, пришедший ради встреч с психологом, попадал в чужой ему инструмент
 * про людей и круги доверия, а психолог — мимо своей практики. Теперь:
 *
 *  - есть профиль психолога (`me.pro`) → практика, `/cabinet/practice`;
 *  - иначе: есть связи с психологами как у клиента И нет ни одной записи в
 *    CRM → свои встречи, `/my`;
 *  - иначе → `/cabinet`, как было.
 *
 * Одно решение на три места: редирект корня (`HomePage`), вход без `next`
 * (`SignInPage`) и ссылки «Кабинет»/логотип вошедшего в шапке витрины.
 *
 * Два запроса идут ПАРАЛЛЕЛЬНО и только ради этого решения; ответ помнится на
 * время жизни вкладки (выход перезагружает страницу — вместе с памятью).
 * Любой отказ или долгое молчание — `/cabinet`, как раньше: решение о
 * маршруте не имеет права задержать вход.
 *
 * Модуль живёт в зоне входа, а не кабинета: его читает витрина, а ей запрещено
 * импортировать кабинет (CLAUDE.md). Поэтому два ответа описаны здесь самым
 * узким видом — ровно те поля, которые решение читает.
 */
import { useEffect, useState } from 'react'
import { request } from '@/shared/api/client'
import type { Account } from './session'

export type HomeRoute = '/cabinet' | '/cabinet/practice' | '/my'

/** Столько решение может ждать ответа, прежде чем вести по-старому. */
export const HOME_DECISION_TIMEOUT_MS = 2500

/** Что решение знает об активности аккаунта. */
export interface Activity {
  hasPsychologists: boolean
  crmRecords: number
}

/** Кто перед нами — ровно то, что решение читает из `/me`. */
type Who = { id: number; pro: Account['pro'] | boolean }

/** Само правило — без сети, для тестов и для чтения. */
export function homeRouteFor(user: Pick<Who, 'pro'>, activity: Activity | null): HomeRoute {
  if (user.pro) return '/cabinet/practice'
  if (activity && activity.hasPsychologists && activity.crmRecords === 0) return '/my'
  return '/cabinet'
}

async function loadActivity(): Promise<Activity> {
  const [relationships, people] = await Promise.all([
    request<{ items: unknown[] }>('/relationships', { query: { side: 'client', size: 1 } }),
    request<{ count: number }>('/people/count'),
  ])
  return { hasPsychologists: relationships.items.length > 0, crmRecords: people.count }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        clearTimeout(timer)
        reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

/** Решение, запомненное для одного аккаунта: второй вызов не ходит в сеть. */
let remembered: { userId: number; route: Promise<HomeRoute> } | null = null

/** Куда вести этого вошедшего. Никогда не отказывает. */
export function resolveHome(user: Who): Promise<HomeRoute> {
  // Психологу сеть не нужна: роль известна из /me.
  if (user.pro) return Promise.resolve('/cabinet/practice')
  if (remembered?.userId !== user.id) {
    remembered = {
      userId: user.id,
      route: withTimeout(loadActivity(), HOME_DECISION_TIMEOUT_MS)
        .then((activity) => homeRouteFor(user, activity))
        .catch(() => '/cabinet' as const),
    }
  }
  return remembered.route
}

/** Для тестов: забыть решение, как забывает его перезагрузка страницы. */
export function forgetHomeDecision(): void {
  remembered = null
}

/**
 * Решение для экрана: `null`, пока его нет (или пока нет вошедшего).
 * Экраны, которым нужно куда-то вести, ждут; ссылки показывают прежний
 * `/cabinet` до ответа.
 */
export function useHomeRoute(user: Who | null): HomeRoute | null {
  const [decided, setDecided] = useState<{ userId: number; route: HomeRoute } | null>(null)
  const userId = user?.id ?? null
  const pro = Boolean(user?.pro)
  useEffect(() => {
    if (userId === null || pro) return undefined
    let live = true
    void resolveHome({ id: userId, pro: false }).then((route) => {
      if (live) setDecided({ userId, route })
    })
    return () => {
      live = false
    }
  }, [userId, pro])
  if (userId === null) return null
  if (pro) return '/cabinet/practice'
  return decided?.userId === userId ? decided.route : null
}
